/**
 * @namespace CompareEvents
 * @description Product comparison state, persisted to localStorage so selections survive
 * navigation across collection, search, and product pages.
 */

const STORAGE_KEY = 'theme:compare';
export const COMPARE_LIMIT = 3;

/**
 * @typedef {Object} CompareItem
 * @property {string} id - Product id.
 * @property {string} handle - Product handle.
 * @property {string} title - Product title.
 * @property {string} image - Product image URL (may be empty).
 * @property {string} url - Product URL.
 * @property {string} type - Product type (Shopify's product.type). Used to keep compare limited to one type at a time.
 */

/**
 * Event dispatched on `document` whenever the compare list changes.
 * @extends {Event}
 */
export class CompareUpdateEvent extends Event {
  static eventName = 'compare:update';

  /** @param {CompareItem[]} items */
  constructor(items) {
    super(CompareUpdateEvent.eventName, { bubbles: true });
    this.detail = { items };
  }
}

/**
 * Event dispatched on `document` to request that the compare drawer open.
 * @extends {Event}
 */
export class CompareOpenEvent extends Event {
  static eventName = 'compare:open';

  constructor() {
    super(CompareOpenEvent.eventName, { bubbles: true });
  }
}

/**
 * Event dispatched on `document` when an add/toggle is rejected (limit reached or product type mismatch).
 * @extends {Event}
 */
export class CompareBlockedEvent extends Event {
  static eventName = 'compare:blocked';

  /** @param {{ reason: 'limit' | 'type-mismatch', existingType?: string }} detail */
  constructor(detail) {
    super(CompareBlockedEvent.eventName, { bubbles: true });
    this.detail = detail;
  }
}

class CompareStore {
  /** @type {CompareItem[]} */
  #items = [];

  constructor() {
    this.#items = this.#read();
  }

  /** @returns {CompareItem[]} */
  #read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#items));
    } catch {
      // localStorage unavailable (private browsing, quota) — state stays in-memory for this page view.
    }
    document.dispatchEvent(new CompareUpdateEvent(this.list()));
  }

  /** @returns {CompareItem[]} */
  list() {
    return [...this.#items];
  }

  /** @returns {number} */
  count() {
    return this.#items.length;
  }

  /** @param {string} id */
  has(id) {
    return this.#items.some((item) => item.id === id);
  }

  /** @returns {string | null} The product type shared by the current selection, or null if empty. */
  currentType() {
    return this.#items[0]?.type ?? null;
  }

  /**
   * @param {CompareItem} item
   * @returns {{ ok: boolean, reason?: 'limit' | 'type-mismatch', existingType?: string }}
   */
  add(item) {
    if (this.has(item.id)) return { ok: true };

    const existingType = this.currentType();
    if (existingType !== null && item.type !== existingType) {
      const result = { ok: false, reason: 'type-mismatch', existingType };
      document.dispatchEvent(new CompareBlockedEvent(result));
      return result;
    }

    if (this.#items.length >= COMPARE_LIMIT) {
      const result = { ok: false, reason: 'limit' };
      document.dispatchEvent(new CompareBlockedEvent(result));
      return result;
    }

    this.#items.push(item);
    this.#write();
    return { ok: true };
  }

  /** @param {string} id */
  remove(id) {
    this.#items = this.#items.filter((item) => item.id !== id);
    this.#write();
  }

  /**
   * @param {CompareItem} item
   * @returns {{ ok: boolean, reason?: 'limit' | 'type-mismatch', existingType?: string }}
   */
  toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return { ok: true };
    }
    return this.add(item);
  }

  clear() {
    this.#items = [];
    this.#write();
  }

  /** Re-reads from localStorage (used when another tab changes the list) and notifies listeners. */
  sync() {
    this.#items = this.#read();
    document.dispatchEvent(new CompareUpdateEvent(this.list()));
  }
}

export const compareStore = new CompareStore();

// Keep multiple open tabs in sync.
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  compareStore.sync();
});
